/* Deck Builder — paste a decklist, resolve cards against the store's real
   Shopify catalog (both listing origins), bulk add to cart.
   Replaces the BinderPOS Multi-Card Search bundle, which searched BinderPOS's
   own inventory DB and could not see app-built listings — see
   docs/DECKLIST-BINDERPOS-LIMITS.md.
   Spec: docs/superpowers/specs/2026-07-17-deck-builder-design.md.
   Pure helpers are module.exports-guarded so `node --test` can import them. */
(function () {
  'use strict';

  const API_VERSION = '2026-04';
  const BATCH_SIZE = 10;      // cards per aliased Storefront request
  const PAGE_CAP = 4;         // max result pages per card (250 each)
  const SCRYFALL_CHUNK = 75;  // /cards/collection identifier cap
  const DRAFT_KEY_PREFIX = 'tgd-deck-builder-draft';
  // Pre-Unit-31 single-key draft — read-only mtg fallback (spec §3).
  const LEGACY_DRAFT_KEY = DRAFT_KEY_PREFIX;

  function draftKeyFor(game) {
    return DRAFT_KEY_PREFIX + ':' + game;
  }

  // Boot game resolution: URL param > data-game attr > 'mtg'; a key is
  // only honoured when it exists in the games map (spec §2).
  function resolveGameKey(paramValue, attrValue, games) {
    if (paramValue && games && games[paramValue]) return paramValue;
    if (attrValue && games && games[attrValue]) return attrValue;
    return 'mtg';
  }

  // Production game gating (spec §1): filter ONCE at boot, BEFORE
  // resolveGameKey, so every downstream consumer (this.games, the select,
  // switchGame's guard) sees only accessible games. Locked without a real
  // mtg entry falls open — boot's per-game validation still governs.
  function accessibleGames(games, unlocked) {
    if (unlocked || !games || !games.mtg) return games;
    return { mtg: games.mtg };
  }

  /* ---------- decklist parser ---------- */

  const SECTION_RE = /^(deck|main ?deck|sideboard|commander|companion|maybeboard)\b:?\s*$/i;
  const QTY_RE = /^(\d+)\s*[xX]?\s+(.+)$/;
  // MTGA export: "4 Lightning Bolt (M11) 123"
  const HINT_RE = /^(.+?)\s+\(([A-Za-z0-9]{2,6})\)\s+([A-Za-z0-9-]+)\s*$/;

  function parseDecklist(text) {
    const byKey = new Map();
    String(text == null ? '' : text).split('\n').forEach(function (line) {
      let s = line.trim();
      if (!s || SECTION_RE.test(s)) return;
      s = s.replace(/^SB:\s*/i, '');
      let quantity = 1;
      let rest = s;
      const m = QTY_RE.exec(s);
      if (m) { quantity = parseInt(m[1], 10); rest = m[2].trim(); }
      if (!quantity) return;
      let name = rest;
      let setHint = null;
      const h = HINT_RE.exec(rest);
      if (h) {
        name = h[1].trim();
        setHint = { set: h[2].toUpperCase(), number: h[3] };
      }
      const key = name.toLowerCase();
      const prev = byKey.get(key);
      if (prev) prev.quantity += quantity;
      else byKey.set(key, { name: name, quantity: quantity, setHint: setHint });
    });
    return Array.from(byKey.values());
  }

  // Did-you-mean line swap: replaces the NAME SEGMENT of a decklist
  // line when it names oldKey, preserving the quantity prefix and any
  // MTGA hint suffix verbatim. Duplicate-name lines each swap (they
  // name the same card — intended).
  function swapNameInLine(line, oldKey, newName) {
    const trimmed = line.trim();
    // Preserve a leading "SB:" sideboard marker (parseDecklist strips it,
    // see ~line 29) verbatim, with its trailing whitespace.
    const sbMatch = trimmed.match(/^SB:\s*/i);
    const sbPrefix = sbMatch ? sbMatch[0] : '';
    const body = trimmed.slice(sbPrefix.length);
    const qm = QTY_RE.exec(body);
    const prefix = qm ? body.slice(0, body.length - qm[2].length) : '';
    const rest = qm ? qm[2] : body;
    const hm = HINT_RE.exec(rest);
    const namePart = hm ? hm[1] : rest;
    if (namePart.trim().toLowerCase() !== oldKey) return line;
    return sbPrefix + prefix + newName + rest.slice(namePart.length);
  }

  /* ---------- title matcher ---------- */

  function normName(s) {
    return String(s).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  // Title up to the first " [" or " (" — the set/qualifier tail starts there.
  function splitTitle(title) {
    const t = String(title);
    let idx = -1;
    [' [', ' ('].forEach(function (d) {
      const i = t.indexOf(d);
      if (i !== -1 && (idx === -1 || i < idx)) idx = i;
    });
    return idx === -1
      ? { head: t.trim(), tail: '' }
      : { head: t.slice(0, idx).trim(), tail: t.slice(idx).trim() };
  }

  // Progressive right-to-left " - suffix" strips; every stage is a candidate.
  function baseNameCandidates(head) {
    const out = [String(head).trim()];
    let t = out[0];
    let i;
    while ((i = t.lastIndexOf(' - ')) !== -1) {
      t = t.slice(0, i).trim();
      out.push(t);
    }
    return out;
  }

  // Canonical name plus, for DFCs, the front face alone (both title forms
  // exist in-store). Returned pre-normalized.
  function acceptedNames(canonicalName) {
    const names = [normName(canonicalName)];
    const parts = String(canonicalName).split(' // ');
    if (parts.length > 1) names.push(normName(parts[0]));
    return names;
  }

  function matchInfo(title, canonicalName) {
    const st = splitTitle(title);
    const names = acceptedNames(canonicalName);
    const cands = baseNameCandidates(st.head);
    for (let k = 0; k < cands.length; k++) {
      if (names.indexOf(normName(cands[k])) !== -1) {
        const removedSuffix = st.head.slice(cands[k].length);
        const foil = /\bfoil\b/i.test(removedSuffix) || /\(foil\)/i.test(st.tail);
        return { finish: foil ? 'Foil' : '' };
      }
    }
    return null;
  }

  /* ---------- Storefront query builders ---------- */

  // The name is spliced inside title:"…" — only the quote and backslash can
  // break out of the phrase. Everything else (apostrophes, //, commas) stays.
  function escapePhrase(text) {
    return String(text == null ? '' : text)
      .replace(/["\\]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function buildCardClause(name, baseQuery, includeUnavailable) {
    return 'title:"' + escapePhrase(name) + '"' +
      (includeUnavailable ? '' : ' AND available_for_sale:true') +
      ' AND (' + baseQuery + ')';
  }

  const PRODUCT_FRAGMENT =
    'fragment P on ProductConnection { pageInfo { hasNextPage endCursor } ' +
    'nodes { id handle title featuredImage { url altText } ' +
    'variants(first: 12) { pageInfo { hasNextPage } ' +
    'nodes { id title availableForSale price { amount currencyCode } } } } }';

  function buildBatchQuery(clauses) {
    const defs = [];
    const fields = [];
    const variables = {};
    clauses.forEach(function (clause, i) {
      defs.push('$q' + i + ': String!');
      fields.push('c' + i + ': products(first: 250, query: $q' + i + ') { ...P }');
      variables['q' + i] = clause;
    });
    return {
      query: 'query Deck(' + defs.join(', ') + ') { ' + fields.join(' ') + ' } ' + PRODUCT_FRAGMENT,
      variables: variables
    };
  }

  function buildPageQuery(clause, cursor) {
    return {
      query: 'query Page($q: String!, $after: String) { products(first: 250, query: $q, after: $after) { ...P } } ' + PRODUCT_FRAGMENT,
      variables: { q: clause, after: cursor }
    };
  }

  /* ---------- printings adapter ---------- */

  function numericId(gid) {
    return String(gid).split('/').pop();
  }

  function formatMoney(n) {
    return '$' + (Math.round(Number(n) * 100) / 100).toFixed(2);
  }

  function sumQuantities(items) {
    return (items || []).reduce(function (s, i) {
      const q = i && Number(i.quantity);
      return s + (isFinite(q) && q > 0 ? q : 0);
    }, 0);
  }

  // Cheapest finite-priced printing (null when none). Single source for
  // both the noStock note's price and the enriched-row link target.
  function cheapestPrinting(printings) {
    let best = null;
    (printings || []).forEach(function (p) {
      if (!p || !isFinite(Number(p.price))) return;
      if (best === null || Number(p.price) < Number(best.price)) best = p;
    });
    return best;
  }

  // Display note for enriched out-of-stock rows (spec §2). Null =
  // caller falls back to the plain "Out of stock".
  function noStockNote(printings) {
    const priced = (printings || []).filter(function (p) {
      return p && isFinite(Number(p.price));
    });
    if (!priced.length) return null;
    const min = Number(cheapestPrinting(priced).price);
    return 'Out of stock — ' + priced.length + ' printing' +
      (priced.length === 1 ? '' : 's') + ' known, from ' + formatMoney(min) + ' when available';
  }

  // Variant-level condition/finish (BinderPOS shape). "Near Mint Foil" →
  // condition "Near Mint", finish "Foil". "Default Title" → no condition.
  function parseVariant(title, conditions) {
    if (title === 'Default Title') return { condition: null, label: null, finish: '' };
    for (let i = 0; i < (conditions || []).length; i++) {
      const c = conditions[i];
      if (title === c.title) return { condition: c.title, label: c.label, finish: '' };
      if (title.indexOf(c.title + ' ') === 0) {
        return { condition: c.title, label: c.label, finish: title.slice(c.title.length + 1) };
      }
    }
    return { condition: null, label: title, finish: '' };
  }

  // Set for display: bracket content (BinderPOS) or the first paren segment
  // of the tail that isn't "(Foil)" (app-built).
  function extractSet(title) {
    const b = /\[([^\]]+)\]/.exec(String(title));
    if (b) return b[1];
    const tail = splitTitle(title).tail;
    const re = /\(([^)]+)\)/g;
    let m;
    while ((m = re.exec(tail))) {
      if (!/^foil$/i.test(m[1])) return m[1];
    }
    return '';
  }

  function adaptCardProducts(nodes, conditions, canonicalName) {
    const printings = [];
    let overflow = false;
    (nodes || []).forEach(function (node) {
      const mi = matchInfo(node.title, canonicalName);
      if (!mi) return;
      if (node.variants && node.variants.pageInfo && node.variants.pageInfo.hasNextPage) overflow = true;
      const set = extractSet(node.title);
      const image = node.featuredImage ? node.featuredImage.url : null;
      ((node.variants && node.variants.nodes) || []).forEach(function (v) {
        const pv = parseVariant(v.title, conditions);
        printings.push({
          variantId: numericId(v.id),
          productId: numericId(node.id),
          handle: node.handle,
          title: node.title,
          set: set,
          image: image,
          finish: pv.finish || mi.finish,
          condition: pv.condition,
          conditionLabel: pv.label,
          price: Number(v.price && v.price.amount),
          available: !!v.availableForSale
        });
      });
    });
    printings.sort(function (a, b) { return a.price - b.price; });
    return { printings: printings, overflow: overflow };
  }

  // The one true hint predicate — selection AND the render-side
  // substitution flag both use it, so they can never disagree.
  function printingMatchesHint(p, setHint) {
    if (!p || !setHint || !setHint.set) return false;
    return String(p.title).toUpperCase().indexOf('(' + setHint.set.toUpperCase() + ')') !== -1;
  }

  function partitionPrintings(printings) {
    const available = [];
    const unavailable = [];
    (printings || []).forEach(function (p) {
      (p && p.available ? available : unavailable).push(p);
    });
    return { available: available, unavailable: unavailable };
  }

  // Gate for the expand button. Deliberately counts ALL printings, not just
  // available ones: a row with one in-stock printing and five sold-out ones
  // still has something worth showing behind "show out of stock".
  function shouldShowPicker(printings) {
    return (printings || []).length > 1;
  }

  function selectPrinting(printings, setHint, overrideVariantId) {
    const avail = (printings || []).filter(function (p) { return p.available; });
    if (overrideVariantId != null) {
      const o = avail.filter(function (p) { return p.variantId === String(overrideVariantId); })[0];
      if (o) return o;
    }
    if (setHint && setHint.set) {
      const h = avail.filter(function (p) { return printingMatchesHint(p, setHint); })[0];
      if (h) return h;
    }
    return avail[0] || null;
  }

  /* ---------- Scryfall layer (skippable catalog assist) ---------- */

  const SCRYFALL_COLLECTION_URL = 'https://api.scryfall.com/cards/collection';

  function chunk(arr, n) {
    const out = [];
    for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
    return out;
  }

  function buildCollectionBody(names) {
    return { identifiers: names.map(function (n) { return { name: n }; }) };
  }

  function cardImage(card) {
    if (card.image_uris && card.image_uris.normal) return card.image_uris.normal;
    if (card.card_faces && card.card_faces[0] && card.card_faces[0].image_uris) {
      return card.card_faces[0].image_uris.normal || null;
    }
    return null;
  }

  // Scryfall returns found cards in identifier order; misses are in not_found.
  function parseCollectionResponse(inputNames, json) {
    const missed = {};
    (((json && json.not_found) || [])).forEach(function (x) {
      missed[normName(x.name || '')] = true;
    });
    const data = (json && json.data) || [];
    const byInput = {};
    const notFound = [];
    let di = 0;
    inputNames.forEach(function (n) {
      if (missed[normName(n)] || di >= data.length) { notFound.push(n); return; }
      const card = data[di++];
      byInput[n.toLowerCase()] = { canonical: card.name, image: cardImage(card) };
    });
    return { byInput: byInput, notFound: notFound };
  }

  function canonicalizeNames(names, deps) {
    const fetchFn = deps.fetchFn;
    const delay = deps.delay || function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };
    const chunks = chunk(names, SCRYFALL_CHUNK);
    const acc = { byInput: {}, notFound: [], failed: false };
    let p = Promise.resolve();
    chunks.forEach(function (part, i) {
      p = p.then(function () {
        return (i === 0 ? Promise.resolve() : delay(110)).then(function () {
          return fetchFn(SCRYFALL_COLLECTION_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(buildCollectionBody(part))
          });
        }).then(function (res) {
          if (!res.ok) throw new Error('scryfall HTTP error');
          return res.json();
        }).then(function (json) {
          const r = parseCollectionResponse(part, json);
          Object.keys(r.byInput).forEach(function (k) { acc.byInput[k] = r.byInput[k]; });
          acc.notFound = acc.notFound.concat(r.notFound);
        });
      });
    });
    return p.then(function () { return acc; })
      .catch(function () { return { byInput: {}, notFound: [], failed: true }; });
  }

  function scryfallSuggestUrl(input) {
    const q = encodeURIComponent(input);
    return [
      'https://api.scryfall.com/cards/named?fuzzy=' + q,
      'https://api.scryfall.com/cards/autocomplete?q=' + q
    ];
  }

  function suggestName(input, fetchFn) {
    const urls = scryfallSuggestUrl(input);
    return fetchFn(urls[0]).then(function (res) {
      if (res.ok) return res.json().then(function (j) { return j.name || null; });
      return fetchFn(urls[1]).then(function (res2) {
        if (!res2.ok) return null;
        return res2.json().then(function (j) { return (j.data && j.data[0]) || null; });
      });
    }).catch(function () { return null; });
  }

  /* ---------- resolver (the seam — spec §Resolver) ---------- */

  function createResolver(cfg) {
    const fetchFn = cfg.fetchFn || (typeof fetch !== 'undefined' ? fetch.bind(typeof window !== 'undefined' ? window : globalThis) : null);
    const delay = cfg.delay || function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };
    const apiVersion = cfg.apiVersion || API_VERSION;

    function storefront(body) {
      return fetchFn('/api/' + apiVersion + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.token
        },
        body: JSON.stringify(body)
      }).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      }).then(function (json) {
        if (json.errors) throw new Error((json.errors[0] && json.errors[0].message) || 'GraphQL error');
        return json.data;
      });
    }

    // Collect every page of one clause (first page already fetched).
    function collectPages(clause, firstConn) {
      let nodes = firstConn.nodes || [];
      let info = firstConn.pageInfo || {};
      let pages = 1;
      function next() {
        if (!info.hasNextPage) return Promise.resolve(nodes);
        if (pages >= PAGE_CAP) {
          console.warn('[deck-builder] page cap hit for clause: ' + clause);
          return Promise.resolve(nodes);
        }
        pages++;
        return storefront(buildPageQuery(clause, info.endCursor)).then(function (data) {
          nodes = nodes.concat(data.products.nodes || []);
          info = data.products.pageInfo || {};
          return next();
        });
      }
      return next();
    }

    // (a) Batch-level fetch-and-adapt seam: ONE batched request for
    // `names`, invoking onCard({name, printings, overflow}) per card as
    // its pages complete. Status computation and result emission stay
    // in the PRIMARY path below — enrichment routed through this seam
    // can never flip a row's status (spec §2).
    function fetchAdaptBatch(names, includeUnavailable, onCard) {
      const clauses = names.map(function (n) {
        return buildCardClause(n, cfg.baseQuery, includeUnavailable);
      });
      const body = buildBatchQuery(clauses);
      function attempt(remaining) {
        return storefront(body).catch(function (err) {
          if (remaining > 0) return storefront(body);
          throw err;
        });
      }
      return attempt(1).then(function (data) {
        return Promise.all(names.map(function (n, i) {
          return collectPages(clauses[i], data['c' + i]).then(function (nodes) {
            const adapted = adaptCardProducts(nodes, cfg.conditions, n);
            // Index passed through so the primary path maps back to its
            // card POSITIONALLY — a byName map would collide when two
            // entries canonicalize to the same searchName.
            onCard({ name: n, printings: adapted.printings, overflow: adapted.overflow }, i);
          });
        }));
      });
    }

    // (b) PRIMARY path: status + emit, behaviour-identical to the
    // pre-split implementation (per-card streaming, retry, partial-emit
    // error handling via card.emitted).
    function searchBatch(cards, emit) {
      return fetchAdaptBatch(cards.map(function (c) { return c.searchName; }), false, function (r, i) {
        const card = cards[i];
        if (r.overflow) console.warn('[deck-builder] variants window overflow for ' + card.searchName);
        const status = r.printings.length
          ? 'matched'
          : (card.verified ? 'noStock' : 'unverified');
        card.emitted = true;
        emit(makeResult(card, status, r.printings));
      }).catch(function () {
        cards.forEach(function (card) {
          if (!card.emitted) emit(makeResult(card, 'error', []));
        });
      });
    }

    function makeResult(card, status, printings) {
      return {
        name: card.entry.name,
        canonical: card.searchName,
        quantity: card.entry.quantity,
        setHint: card.entry.setHint || null,
        status: status,
        printings: printings,
        suggestion: card.suggestion || null,
        image: card.image || null,
        verified: !!card.verified
      };
    }

    function resolve(entries, hooks) {
      const onResult = (hooks && hooks.onResult) || function () {};
      const results = [];
      const emit = function (r) { results.push(r); onResult(r); };

      const assistWanted = cfg.catalogAssist === 'scryfall';
      const assistPromise = assistWanted
        ? canonicalizeNames(entries.map(function (e) { return e.name; }), { fetchFn: fetchFn, delay: delay })
        : Promise.resolve({ byInput: {}, notFound: [], failed: true });

      return assistPromise.then(function (assist) {
        const usedAssist = assistWanted && !assist.failed;
        const cards = [];
        let unknownChain = Promise.resolve();
        entries.forEach(function (entry) {
          const hit = usedAssist ? assist.byInput[entry.name.toLowerCase()] : null;
          if (usedAssist && !hit) {
            // real miss from a working assist: unknown name + one suggestion
            unknownChain = unknownChain.then(function () {
              return delay(110).then(function () {
                return suggestName(entry.name, fetchFn);
              }).then(function (s) {
                emit(makeResult({ entry: entry, searchName: entry.name, verified: false, suggestion: s }, 'unknownName', []));
              });
            });
            return;
          }
          cards.push({
            entry: entry,
            searchName: hit ? hit.canonical : entry.name,
            image: hit ? hit.image : null,
            verified: !!hit
          });
        });

        const batches = chunk(cards, BATCH_SIZE);
        let searchChain = Promise.resolve();
        batches.forEach(function (batch) {
          searchChain = searchChain.then(function () { return searchBatch(batch, emit); });
        });

        return Promise.all([unknownChain, searchChain]).then(function () {
          return { results: results, usedAssist: usedAssist };
        });
      });
    }

    return {
      resolve: resolve,
      // Enrichment seam (spec §2): availability-inclusive printings per
      // name, chunked like the primary path. Never emits results.
      fetchPrintings: function (names) {
        const out = [];
        let chain = Promise.resolve();
        chunk(names, BATCH_SIZE).forEach(function (group) {
          chain = chain.then(function () {
            return fetchAdaptBatch(group, true, function (r) { out.push(r); });
          }).catch(function () {});
        });
        return chain.then(function () { return out; });
      }
    };
  }

  /* ---------- row state ---------- */

  function rowKeyFor(result) {
    return String(result.canonical || result.name).toLowerCase();
  }

  function makeRow(result, prev) {
    const row = {
      key: rowKeyFor(result),
      name: result.canonical || result.name,
      quantity: result.quantity,
      userQty: prev && prev.userQty != null ? prev.userQty : null,
      setHint: result.setHint || null,
      status: result.status,
      printings: result.printings || [],
      override: prev ? prev.override || null : null,
      suggestion: result.suggestion || null,
      image: result.image || null,
      verified: result.verified !== false,
      cartError: null
    };
    row.selected = selectPrinting(row.printings, row.setHint, row.override);
    return row;
  }

  function rowQty(row) {
    return row.userQty != null ? row.userQty : row.quantity;
  }

  function totals(rows) {
    let price = 0;
    let inStock = 0;
    let items = 0;
    rows.forEach(function (row) {
      if (row.status === 'matched' && row.selected) {
        inStock++;
        items += rowQty(row);
        price += row.selected.price * rowQty(row);
      }
    });
    return { price: Math.round(price * 100) / 100, inStock: inStock, total: rows.length, items: items };
  }

  function buildCartItems(rows) {
    const items = [];
    rows.forEach(function (row) {
      if (row.status === 'matched' && row.selected && row.selected.available) {
        items.push({ id: Number(row.selected.variantId), quantity: rowQty(row) });
      }
    });
    return items;
  }

  /* ---------- cart chunker ---------- */

  function postCart(items, fetchFn) {
    return fetchFn('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (res.ok) return { ok: true };
      return res.json().catch(function () { return {}; }).then(function (j) {
        return { ok: false, message: j.description || j.message || ('HTTP ' + res.status) };
      });
    });
  }

  // Multi-item /cart/add.js is all-or-nothing: bisect on failure so a single
  // sold-out line can't block the rest of the deck.
  function addItemsWithFallback(items, fetchFn) {
    return postCart(items, fetchFn).then(function (r) {
      if (r.ok) return { added: items, failed: [] };
      if (items.length === 1) return { added: [], failed: [{ item: items[0], message: r.message }] };
      const mid = Math.ceil(items.length / 2);
      return addItemsWithFallback(items.slice(0, mid), fetchFn).then(function (a) {
        return addItemsWithFallback(items.slice(mid), fetchFn).then(function (b) {
          return { added: a.added.concat(b.added), failed: a.failed.concat(b.failed) };
        });
      });
    });
  }

  /* ---------- draft persistence ---------- */

  function serializeDraft(d) {
    return JSON.stringify({
      v: 1,
      game: d.game,
      text: d.text,
      overrides: d.overrides || {},
      qtys: d.qtys || {}
    });
  }

  function parseDraft(raw) {
    try {
      const d = JSON.parse(raw);
      return d && d.v === 1 && typeof d.text === 'string' ? d : null;
    } catch (e) {
      return null;
    }
  }

  /* ---------- html escaping ---------- */

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Paste-hint copy per game. The MTGA-export sentence is mtg-only —
  // it would mislead users of every other game (spec §4c). The format
  // example stays everywhere: it teaches the line format, not the game.
  function hintHtml(gameKey) {
    return 'Paste your decklist — one card per line, like ' +
      '<code>4 Lightning Bolt</code>.' +
      (gameKey === 'mtg' ? ' MTG Arena exports work too.' : '');
  }

  const api = {
    API_VERSION: API_VERSION,
    BATCH_SIZE: BATCH_SIZE,
    PAGE_CAP: PAGE_CAP,
    SCRYFALL_CHUNK: SCRYFALL_CHUNK,
    LEGACY_DRAFT_KEY: LEGACY_DRAFT_KEY,
    draftKeyFor: draftKeyFor,
    resolveGameKey: resolveGameKey,
    accessibleGames: accessibleGames,
    parseDecklist: parseDecklist,
    normName: normName,
    splitTitle: splitTitle,
    baseNameCandidates: baseNameCandidates,
    acceptedNames: acceptedNames,
    matchInfo: matchInfo,
    escapePhrase: escapePhrase,
    buildCardClause: buildCardClause,
    buildBatchQuery: buildBatchQuery,
    buildPageQuery: buildPageQuery,
    numericId: numericId,
    formatMoney: formatMoney,
    parseVariant: parseVariant,
    extractSet: extractSet,
    adaptCardProducts: adaptCardProducts,
    selectPrinting: selectPrinting,
    SCRYFALL_COLLECTION_URL: SCRYFALL_COLLECTION_URL,
    chunk: chunk,
    buildCollectionBody: buildCollectionBody,
    parseCollectionResponse: parseCollectionResponse,
    canonicalizeNames: canonicalizeNames,
    scryfallSuggestUrl: scryfallSuggestUrl,
    suggestName: suggestName,
    createResolver: createResolver,
    rowKeyFor: rowKeyFor,
    makeRow: makeRow,
    rowQty: rowQty,
    totals: totals,
    buildCartItems: buildCartItems,
    addItemsWithFallback: addItemsWithFallback,
    serializeDraft: serializeDraft,
    parseDraft: parseDraft,
    escapeHtml: escapeHtml,
    hintHtml: hintHtml,
    printingMatchesHint: printingMatchesHint,
    partitionPrintings: partitionPrintings,
    shouldShowPicker: shouldShowPicker,
    sumQuantities: sumQuantities,
    noStockNote: noStockNote,
    swapNameInLine: swapNameInLine,
    cheapestPrinting: cheapestPrinting
  };

  /* ---------- DOM component (browser only) ---------- */
  if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined' && window.customElements) {

    const warn = function (msg) { console.warn('[deck-builder] ' + msg); };

    const readJson = function (el) {
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    };

    const toast = function (msg, tone) {
      const T = window.Toast;
      if (T && tone === 'error' && T.error) return T.error(msg);
      if (T && tone === 'success' && T.success) return T.success(msg);
      if (T && T.show) return T.show(msg);
      warn(msg);
    };

    const storage = {
      get: function (k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
      set: function (k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
    };

    class DeckBuilderElement extends HTMLElement {
      connectedCallback() {
        if (this._booted) return;
        this._booted = true;
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.boot.bind(this));
        } else {
          this.boot();
        }
      }

      boot() {
        let games = readJson(document.getElementById('deck-builder-games'));
        const params = new URLSearchParams(window.location.search);
        // Tester flag (spec §1): ?games=all unlocks every configured game.
        // Reassigning the ONE local keeps every consumer below filtered —
        // do not fork this into a second variable.
        games = accessibleGames(games, params.get('games') === 'all');
        const gameKey = resolveGameKey(params.get('game'), this.getAttribute('data-game'), games);
        const game = games && games[gameKey];
        const token = window.theme && window.theme.storefront && window.theme.storefront.token;
        if (!game || typeof game.baseQuery !== 'string' || !Array.isArray(game.conditions)) {
          return warn('missing or invalid config for game "' + gameKey + '"');
        }
        if (!token) return warn('no storefront token configured');

        this.game = gameKey;
        this.cfg = game;
        this.games = games;
        this._token = token; // resolver rebuilds on game switch re-supply this
        this.assist = game.catalogAssist || null; // read by the verified-note rule on FIRST render
        this.resolver = createResolver({
          token: token,
          baseQuery: game.baseQuery,
          conditions: game.conditions,
          catalogAssist: game.catalogAssist || null
        });
        this.rows = [];
        this.resolving = false;
        this.usedAssist = false;
        this._resolveId = 0;
        this._draftPrefs = { overrides: {}, qtys: {} };

        this.renderShell();
        this.bindEvents();     // Task 10
        this.offerDraft();     // Task 10
      }

      renderShell() {
        // One accessible game (production-locked) → no selector at all,
        // not a one-option dropdown (spec §1).
        const gameControl = Object.keys(this.games).length > 1
          ? '<label class="db-game-label">Game ' +
              '<select class="db-game" data-db-game>' + this.gameOptionsHtml() + '</select>' +
            '</label>'
          : '';
        this.innerHTML =
          '<div class="db-shell">' +
            '<div class="db-input">' +
              gameControl +
              '<p class="db-hint">' + hintHtml(this.game) + '</p>' +
              '<div class="db-resume" data-db-resume-wrap hidden>' +
                '<button type="button" class="db-btn" data-db-resume>Resume your last deck</button>' +
              '</div>' +
              '<textarea class="db-textarea" data-db-text rows="10" ' +
                'placeholder="4 Lightning Bolt&#10;1 Sol Ring"></textarea>' +
              '<button type="button" class="db-btn db-btn--primary" data-db-search>Find cards</button>' +
            '</div>' +
            // polite live region: per-card updates coalesce in SRs while
            // speaking — deliberate, do not switch to terminal-only (spec §4b)
            '<p class="db-progress" data-db-progress aria-live="polite" hidden></p>' +
            '<ul class="db-rows" data-db-rows></ul>' +
            '<p class="db-attribution" data-db-attribution hidden>Card names and images ' +
              'supplemented by <a href="https://scryfall.com" target="_blank" rel="noopener">Scryfall</a>.</p>' +
            '<div class="db-footer" data-db-footer hidden>' +
              '<div class="db-footer__totals" data-db-totals></div>' +
              '<button type="button" class="db-btn db-btn--primary" data-db-add>Add deck to cart</button>' +
            '</div>' +
          '</div>';
      }

      gameOptionsHtml() {
        const self = this;
        return Object.keys(this.games).map(function (k) {
          return '<option value="' + escapeHtml(k) + '"' + (k === self.game ? ' selected' : '') + '>' +
            escapeHtml(self.games[k].label) + '</option>';
        }).join('');
      }

      // Game switch — the 6-step order is load-bearing (spec §2):
      // save-first under the OLD game, then a COMPLETE reset.
      switchGame(newKey) {
        if (!this.games[newKey] || newKey === this.game) return;
        // 1. Kill the pending autosave (it would fire post-switch and
        //    clobber the NEW game's stored draft), then save the old
        //    game's text — this.game is still the old key here. GUARDED:
        //    an EMPTY textarea must never save — the offered-but-
        //    unresumed case (Resume button shown, textarea untouched)
        //    would otherwise overwrite the old game's stored draft with
        //    an empty one, silently destroying it AND shadowing the
        //    legacy-key fallback forever.
        clearTimeout(this._draftTimer);
        if (this.querySelector('[data-db-text]').value.trim()) this.saveDraft();
        // 2. New config + resolver; token re-supplied (not part of cfg).
        const game = this.games[newKey];
        this.game = newKey;
        this.cfg = game;
        this.assist = game.catalogAssist || null;
        // Keep the select in sync when switchGame is called programmatically
        // (deep-link / draft resume), not just from the change event.
        var sel = this.querySelector('[data-db-game]');
        if (sel) sel.value = newKey;
        this.resolver = createResolver({
          token: this._token,
          baseQuery: game.baseQuery,
          conditions: game.conditions,
          catalogAssist: game.catalogAssist || null
        });
        // Shell is never rebuilt on switch; refresh the one game-dependent
        // static string. Explicit newKey — this.game is only trustworthy
        // post-swap (spec §4c).
        var hintEl = this.querySelector('.db-hint');
        if (hintEl) hintEl.innerHTML = hintHtml(newKey);
        // 3. Cancels any in-flight resolve/enrichment from the old game
        //    (their callbacks re-check this id).
        ++this._resolveId;
        // 4. Full per-search reset. usedAssist is MONOTONIC — without
        //    this the Scryfall attribution sticks forever.
        this.rows = [];
        this.resolving = false;
        this.usedAssist = false;
        this._draftPrefs = { overrides: {}, qtys: {} };
        this.querySelector('[data-db-text]').value = '';
        this.querySelector('[data-db-progress]').hidden = true;
        this.renderResults();
        // 5. Deep-linkable, no history spam.
        const params = new URLSearchParams(window.location.search);
        params.set('game', newKey);
        history.replaceState(null, '', window.location.pathname + '?' + params.toString() + window.location.hash);
        // 6. The new game's own draft (offerDraft is reset-first).
        this.offerDraft();
      }

      onSearch() {
        const text = this.querySelector('[data-db-text]').value;
        const entries = parseDecklist(text);
        if (!entries.length) return toast('Nothing to search — paste a decklist first.', 'error');

        // carry user state across re-resolves (spec reconciliation rule)
        const prevByKey = {};
        this.rows.forEach(function (row) {
          prevByKey[row.key] = { override: row.override, userQty: row.userQty };
        });
        const draftPrefs = this._draftPrefs;
        Object.keys(draftPrefs.overrides).forEach(function (k) {
          prevByKey[k] = prevByKey[k] || { override: draftPrefs.overrides[k], userQty: null };
        });
        Object.keys(draftPrefs.qtys).forEach(function (k) {
          prevByKey[k] = prevByKey[k] || { override: null, userQty: null };
          if (prevByKey[k].userQty == null) prevByKey[k].userQty = draftPrefs.qtys[k];
        });
        this._draftPrefs = { overrides: {}, qtys: {} };

        const id = ++this._resolveId;
        const self = this;
        this.rows = [];
        this.resolving = true;
        this.expectedCount = entries.length;
        this.renderResults();

        this.resolver.resolve(entries, {
          onResult: function (result) {
            if (id !== self._resolveId) return;
            self.upsertRow(makeRow(result, prevByKey[rowKeyFor(result)] || null));
            self.renderResults();
          }
        }).then(function (summary) {
          if (id !== self._resolveId) return;
          self.resolving = false;
          self.usedAssist = self.usedAssist || summary.usedAssist;
          self.renderResults();
          self.enrichNoStock(id);
        }).catch(function (err) {
          if (id !== self._resolveId) return;
          self.resolving = false;
          self.renderResults();
          toast('Search failed: ' + err.message, 'error');
        });

        this.saveDraft(); // Task 10
      }

      // Out-of-stock enrichment (spec §2): one availability-inclusive
      // batched fetch for noStock names, attaching printings for
      // DISPLAY only — status/selected untouched; buildCartItems already
      // skips these rows. Silent on failure; stale runs discarded via
      // the same _resolveId guard as the primary flow.
      enrichNoStock(id) {
        const names = this.rows.filter(function (r) {
          return r.status === 'noStock' && !(r.printings && r.printings.length);
        }).map(function (r) { return r.name; });
        if (!names.length) return;
        const self = this;
        this.resolver.fetchPrintings(names).then(function (results) {
          if (id !== self._resolveId) return;
          const byKey = {};
          results.forEach(function (r) {
            byKey[String(r.name).toLowerCase()] = r.printings;
          });
          let changed = false;
          self.rows.forEach(function (row) {
            const p = byKey[row.key];
            if (row.status === 'noStock' && p && p.length) {
              row.printings = p;
              changed = true;
            }
          });
          if (changed) self.renderResults();
        }).catch(function () {});
      }

      // Single-entry re-resolves (did-you-mean, retry) update in place.
      upsertRow(row) {
        for (let i = 0; i < this.rows.length; i++) {
          if (this.rows[i].key === row.key) { this.rows[i] = row; return; }
        }
        this.rows.push(row);
      }

      renderResults() {
        const progress = this.querySelector('[data-db-progress]');
        const footer = this.querySelector('[data-db-footer]');
        const attribution = this.querySelector('[data-db-attribution]');
        const list = this.querySelector('[data-db-rows]');
        const self = this;

        progress.hidden = !this.resolving;
        if (this.resolving) {
          progress.textContent = this.rows.length + ' / ' + this.expectedCount + ' cards checked…';
        }
        list.innerHTML = this.rows.map(function (row) { return self.renderRow(row); }).join('');

        attribution.hidden = !this.usedAssist;
        const t = totals(this.rows);
        footer.hidden = !this.rows.length;
        this.querySelector('[data-db-totals]').innerHTML =
          '<strong>' + escapeHtml(formatMoney(t.price)) + '</strong> · ' +
          t.inStock + ' of ' + t.total + ' cards in stock · ' + t.items + ' items';
        this.querySelector('[data-db-add]').disabled = !buildCartItems(this.rows).length;
      }

      renderRow(row) {
        const k = escapeHtml(row.key);
        if (row.status === 'matched' && row.selected) return this.renderMatchedRow(row, k);

        let note;
        let extra = '';
        if (row.status === 'noStock') {
          note = noStockNote(row.printings) || 'Out of stock';
        } else if (row.status === 'unknownName') {
          note = 'Card not found';
          if (row.suggestion) {
            extra = '<button type="button" class="db-btn db-btn--small" data-db-fix data-key="' + k +
              '" data-suggestion="' + escapeHtml(row.suggestion) + '">Did you mean “' +
              escapeHtml(row.suggestion) + '”?</button>';
          }
        } else if (row.status === 'error') {
          note = 'Search failed';
          extra = '<button type="button" class="db-btn db-btn--small" data-db-fix data-key="' + k +
            '" data-suggestion="' + escapeHtml(row.name) + '">Retry</button>';
        } else {
          note = 'Not found (name checking unavailable)';
        }
        const img = row.image
          ? '<img class="db-row__img" loading="lazy" src="' + escapeHtml(row.image) + '" alt="">'
          : '<span class="db-row__img db-row__img--empty"></span>';
        let nameInner = escapeHtml(rowQty(row)) + '× ' + escapeHtml(row.name);
        if (row.status === 'noStock' && row.printings && row.printings.length) {
          const cheapest = cheapestPrinting(row.printings);
          if (cheapest) {
            nameInner = escapeHtml(rowQty(row)) + '× <a class="db-row__name-link" href="/products/' +
              escapeHtml(cheapest.handle) + '">' + escapeHtml(row.name) + '</a>';
          }
        }
        return '<li class="db-row db-row--' + escapeHtml(row.status) + '">' + img +
          '<div class="db-row__main"><span class="db-row__name">' + nameInner + '</span>' +
          '<span class="db-row__note">' + note + '</span>' + extra + '</div></li>';
      }

      renderMatchedRow(row, k) {
        const sel = row.selected;
        const img = sel.image || row.image;
        const parts = partitionPrintings(row.printings);
        // Two labels, because the row means different things: with several in
        // stock it's a price-shopping affordance; with one it's the only way
        // to see the sold-out printings at all.
        const expandLabel = parts.available.length > 1
          ? parts.available.length + ' printings from ' + escapeHtml(formatMoney(parts.available[0].price))
          : parts.unavailable.length + ' other printing' + (parts.unavailable.length === 1 ? '' : 's') + ', out of stock';
        const expandBtn = shouldShowPicker(row.printings)
          ? '<button type="button" class="db-btn db-btn--small db-row__expand' +
            (row.expanded ? ' is-open' : '') + '" data-db-expand data-key="' + k +
            '" aria-expanded="' + (row.expanded ? 'true' : 'false') + '">' +
            expandLabel + ' <span class="db-chevron" aria-hidden="true"></span></button>'
          : '';
        const picker = row.expanded ? this.renderPicker(row, k) : '';
        const overrideHonoured = row.override != null && sel && sel.variantId === String(row.override);
        const substituted = row.setHint && sel && !printingMatchesHint(sel, row.setHint) && !overrideHonoured
          ? '<span class="db-row__note">requested ' + escapeHtml(row.setHint.set.toUpperCase()) +
            ' unavailable — substituted</span>'
          : '';
        // Store-search-only games have no assist that COULD verify —
        // the tag would brand every match, misleadingly (spec §4).
        const verifiedNote = (row.verified || !this.assist) ? '' : '<span class="db-row__note">name unverified</span>';
        const cartError = row.cartError ? '<span class="db-row__note db-row__note--error">' + escapeHtml(row.cartError) + '</span>' : '';
        return '<li class="db-row db-row--matched">' +
          (img ? '<img class="db-row__img" loading="lazy" src="' + escapeHtml(img) + '" alt="">'
               : '<span class="db-row__img db-row__img--empty"></span>') +
          '<div class="db-row__main">' +
            '<a class="db-row__name" href="/products/' + escapeHtml(sel.handle) + '">' + escapeHtml(row.name) + '</a>' +
            '<span class="db-row__printing">' + escapeHtml(sel.set) +
              (sel.finish ? ' · ' + escapeHtml(sel.finish) : '') +
              (sel.conditionLabel ? ' · ' + escapeHtml(sel.conditionLabel) : '') +
              ' · ' + escapeHtml(formatMoney(sel.price)) + '</span>' +
            substituted +
            verifiedNote + cartError + expandBtn + picker +
          '</div>' +
          '<div class="db-row__qty">' +
            '<button type="button" class="db-btn db-btn--small" data-db-minus data-key="' + k + '" aria-label="decrease quantity">−</button>' +
            '<input class="db-qty-input" data-db-qty data-key="' + k + '" type="number" min="1" aria-label="Quantity" value="' + escapeHtml(rowQty(row)) + '">' +
            '<button type="button" class="db-btn db-btn--small" data-db-plus data-key="' + k + '" aria-label="increase quantity">+</button>' +
          '</div>' +
          '<div class="db-row__line">' + escapeHtml(formatMoney(sel.price * rowQty(row))) + '</div>' +
        '</li>';
      }

      renderPicker(row, k) {
        const sel = row.selected;
        const parts = partitionPrintings(row.printings);
        // Out-of-stock printings are hidden until asked for: they can't be
        // picked, so by default they're noise between the ones that can.
        const shown = row.showOos ? parts.available.concat(parts.unavailable) : parts.available;
        const oosToggle = parts.unavailable.length
          ? '<li><button type="button" class="db-pill db-pill--oos-toggle" data-db-oos data-key="' + k +
            '" aria-expanded="' + (row.showOos ? 'true' : 'false') + '">' +
            (row.showOos ? 'Hide out of stock' : '+' + parts.unavailable.length + ' out of stock') +
            '</button></li>'
          : '';
        return '<ul class="db-picker">' + shown.map(function (p) {
          const active = sel && p.variantId === sel.variantId;
          const thumb = p.image
            ? '<img class="db-pill__img" loading="lazy" src="' + escapeHtml(p.image) + '" alt="">'
            : '<span class="db-pill__img db-pill__img--empty"></span>';
          const label = escapeHtml(p.set) +
            (p.finish ? ' · ' + escapeHtml(p.finish) : '') +
            (p.conditionLabel ? ' · ' + escapeHtml(p.conditionLabel) : '') +
            ' · ' + escapeHtml(formatMoney(p.price));
          return '<li><button type="button" class="db-pill' + (active ? ' db-pill--active' : '') + '"' +
            (p.available ? '' : ' disabled') +
            ' data-db-pick data-key="' + k + '" data-variant="' + escapeHtml(p.variantId) + '">' +
            thumb + label + (p.available ? '' : ' · out of stock') + '</button></li>';
        }).join('') + oosToggle + '</ul>';
      }

      bindEvents() {
        const self = this;
        this.addEventListener('click', function (e) {
          const t = e.target.closest('[data-db-search],[data-db-resume],[data-db-add],[data-db-expand],[data-db-oos],[data-db-pick],[data-db-plus],[data-db-minus],[data-db-fix]');
          if (!t) return;
          const key = t.getAttribute('data-key');
          const row = self.rows.filter(function (r) { return r.key === key; })[0];
          if (t.hasAttribute('data-db-search')) return self.onSearch();
          if (t.hasAttribute('data-db-resume')) return self.resumeDraft();
          if (t.hasAttribute('data-db-add')) return self.onAddToCart(t);
          if (!row) return;
          if (t.hasAttribute('data-db-expand')) {
            row.expanded = !row.expanded;
            // Per-row and transient: collapsing the picker forgets the reveal,
            // so reopening always starts from the in-stock printings.
            if (!row.expanded) row.showOos = false;
          } else if (t.hasAttribute('data-db-oos')) {
            row.showOos = !row.showOos;
          } else if (t.hasAttribute('data-db-pick')) {
            row.override = t.getAttribute('data-variant');
            row.selected = selectPrinting(row.printings, row.setHint, row.override);
            row.expanded = false;
            row.showOos = false;
          } else if (t.hasAttribute('data-db-plus')) {
            row.userQty = rowQty(row) + 1;
          } else if (t.hasAttribute('data-db-minus')) {
            row.userQty = Math.max(1, rowQty(row) - 1);
          } else if (t.hasAttribute('data-db-fix')) {
            return self.applyFix(row, t.getAttribute('data-suggestion'));
          }
          self.renderResults();
          self.saveDraft();
        });

        this.addEventListener('change', function (e) {
          if (e.target.matches('[data-db-game]')) return self.switchGame(e.target.value);
          if (!e.target.matches('[data-db-qty]')) return;
          const key = e.target.getAttribute('data-key');
          const row = self.rows.filter(function (r) { return r.key === key; })[0];
          if (!row) return;
          const n = parseInt(e.target.value, 10);
          row.userQty = isNaN(n) || n < 1 ? 1 : n;
          self.renderResults();
          self.saveDraft();
        });

        this._draftTimer = null;
        this.querySelector('[data-db-text]').addEventListener('input', function () {
          clearTimeout(self._draftTimer);
          // Instance field so switchGame can cancel a pending save that
          // would otherwise fire post-switch under the NEW game's key.
          self._draftTimer = setTimeout(function () { self.saveDraft(); }, 500);
        });
      }

      // Did-you-mean / retry: swap the row's line in the textarea (keeps the
      // draft coherent) and re-resolve just that entry (resolver subset call).
      applyFix(row, newName) {
        const textarea = this.querySelector('[data-db-text]');
        const lines = textarea.value.split('\n').map(function (line) {
          return swapNameInLine(line, row.key, newName);
        });
        textarea.value = lines.join('\n');

        const self = this;
        const id = this._resolveId;
        const entry = { name: newName, quantity: rowQty(row), setHint: row.setHint || null };
        this.rows = this.rows.filter(function (r) { return r.key !== row.key; });
        this.renderResults();
        this.resolver.resolve([entry], {
          onResult: function (result) {
            if (id !== self._resolveId) return;
            self.upsertRow(makeRow(result, null));
            self.renderResults();
          }
        }).catch(function () { toast('Could not re-check ' + newName, 'error'); });
        this.saveDraft();
      }

      onAddToCart(button) {
        const self = this;
        const items = buildCartItems(this.rows);
        if (!items.length) return toast('Nothing in stock to add.', 'error');
        button.disabled = true;
        button.textContent = 'Adding…';
        addItemsWithFallback(items, fetch.bind(window)).then(function (r) {
          button.disabled = false;
          button.textContent = 'Add deck to cart';
          const failedIds = {};
          r.failed.forEach(function (f) { failedIds[String(f.item.id)] = f.message; });
          self.rows.forEach(function (row) {
            row.cartError = (row.selected && failedIds[row.selected.variantId]) || null;
          });
          self.renderResults();
          const addedCount = sumQuantities(r.added);
          if (!r.failed.length) {
            toast('Deck added to cart — ' + addedCount + ' cards', 'success');
          } else if (r.added.length) {
            toast('Added ' + sumQuantities(r.added) + ' of ' + sumQuantities(items) + ' cards — the rest are unavailable');
          } else {
            toast('Could not add the deck: ' + r.failed[0].message, 'error');
          }
          if (window.Shopify && typeof window.Shopify.adjustCartDropDown === 'function') {
            window.Shopify.adjustCartDropDown();
          }
        }).catch(function (err) {
          button.disabled = false;
          button.textContent = 'Add deck to cart';
          toast('Could not add the deck: ' + err.message, 'error');
        });
      }

      saveDraft() {
        const overrides = {};
        const qtys = {};
        this.rows.forEach(function (row) {
          if (row.override) overrides[row.key] = row.override;
          if (row.userQty != null) qtys[row.key] = row.userQty;
        });
        storage.set(draftKeyFor(this.game), serializeDraft({
          game: this.game,
          text: this.querySelector('[data-db-text]').value,
          overrides: overrides,
          qtys: qtys
        }));
      }

      offerDraft() {
        // Reset-first: safe to re-invoke on every game switch — without
        // this, switching from a game WITH a draft to one WITHOUT leaves
        // the old Resume button live (spec §2 step 6).
        this._draft = null;
        this.querySelector('[data-db-resume-wrap]').hidden = true;
        let d = parseDraft(storage.get(draftKeyFor(this.game)));
        // Legacy pre-Unit-31 drafts live under the un-suffixed key and
        // were always serialized with game:'mtg' — read-only fallback.
        if (!d && this.game === 'mtg') d = parseDraft(storage.get(LEGACY_DRAFT_KEY));
        if (!d || !d.text.trim() || d.game !== this.game) return;
        this._draft = d;
        this.querySelector('[data-db-resume-wrap]').hidden = false;
      }

      resumeDraft() {
        if (!this._draft) return;
        this.querySelector('[data-db-text]').value = this._draft.text;
        this._draftPrefs = { overrides: this._draft.overrides || {}, qtys: this._draft.qtys || {} };
        this.querySelector('[data-db-resume-wrap]').hidden = true;
        this.onSearch();
      }
    }

    window.customElements.define('deck-builder', DeckBuilderElement);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.DeckBuilder = api;
})();
